import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, 
  BarChart, Bar, XAxis, YAxis, CartesianGrid 
} from 'recharts';
import { 
  Plus, TrendingUp, DollarSign, AlertCircle, RefreshCw, Trash2, 
  X, Calendar, CreditCard, ScanEye, Search,ArrowDownCircle, 
  ArrowUpCircle, Home, PieChart as PieChartIcon, PiggyBank,Bitcoin,Eye, EyeOff
} from 'lucide-react';
import Swal from 'sweetalert2';

// IMPORTAMOS NUESTRO CEREBRO DE CONEXIÓN
import { apiFetch } from '../utils/api.ts'; 
import CryptoCarousel from "./CryptoCarousel.tsx";
import "./Dashboard.css"; // 
import FormularioActivos from './FormularioActivo.tsx';
import Billetera from './Billetera.tsx';

// --- INTERFACES ---
interface Movimiento {
  id_movimiento?: number;
  ID_MOVIMIENTO?: number; 
  fecha?: string;
  FECHA?: string;
  monto?: number | string;
  MONTO?: number | string;
  categoria?: string;
  CATEGORIA?: string;
  descripcion?: string;
  DESCRIPCION?: string;
  tipo_gasto?:string;
  TIPO_gasto?: string;
  tipo_movimiento?: string;
  TIPO_movimiento?: string;
  medio_pago?: string;
  MEDIO_PAGO?: string;
}

interface FormData {
  fecha: string;
  tipo_movimiento:string;
  categoria: string;
  medio_pago: string;
  tipo_gasto: string;
  monto: string;
  descripcion: string;
}

const COLORS = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40', '#FF6384'];
function Dashboard() {
  const navigate = useNavigate();

  // --- DATOS USUARIO ---
  const usuario = JSON.parse(localStorage.getItem("usuario") || "{}");
  const nombreAMostrar = usuario?.nombre_completo || "Usuario";
  const [misActivos, setMisActivos] = useState([]);
  const [usuarioId, setUsuarioId] = useState(1); // Esto vendría de tu login/Auth

  // --- ESTADOS ---
   const [totalPatrimonio, setTotalPatrimonio] = useState(0); // <-- AGREGÁ ESTA LÍNEA
  const [ocultarDinero, setOcultarDinero] = useState(false);
  const [modo, setModo] = useState<'GASTO' | 'INGRESO'>('INGRESO');
  const [vistaActual, setVistaActual] = useState<'inicio' | 'Registrar' |'balance'>('inicio'); // <-- ESTADO DEL MENÚ
  const [expenses, setExpenses] = useState<Movimiento[]>([]);
  const [categorias, setCategorias] = useState<string[]>([]);
  const [tipo_movimiento, setTipo_movimiento] = useState<string[]>([]);
  const [mediosPago, setMediosPago] = useState<string[]>([]);
  
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  
  // Filtros y Carrusel
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [currentSlide, setCurrentSlide] = useState(0); // 0 = Mes, 1 = Histórico

  // Formulario
  const [formData, setFormData] = useState<FormData>({
    fecha: new Date().toISOString().slice(0, 10),
    tipo_movimiento: '',
    categoria: '',
    medio_pago: '',
    tipo_gasto: 'VARIABLE',
    monto: '',
    descripcion: ''
  });


const cargarDatosCartera = async () => {
  try {
    // 1. Usamos apiFetch: ya tiene la URL base y pone el Token solo
    const response = await apiFetch('/activos'); 

    if (response.ok) {
      const data = await response.json();
      // 2. Validamos que data tenga lo que esperamos
      setMisActivos(data.activos || []);
      setTotalPatrimonio(data.total_patrimonio_usd || 0);
    } else {
      console.error("Error al obtener activos:", response.status);
    }
  } catch (error) {
    console.error("Error de red en Dashboard:", error);
  }
};

// CORRECCIÓN DEL EFFECT:
useEffect(() => {
  const token = localStorage.getItem('token');
  if (token) {
    cargarDatosCartera();
  } else {
    console.warn("No hay token, redirigiendo...");

  }
}, []); // Se ejecuta una sola vez al cargar el componente

  // --- CARGA DE DATOS (AL INICIAR) ---
  useEffect(() => {
    loadAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


    // --- SINCRONIZACIÓN AUTOMÁTICA DEL MODO ---
  useEffect(() => {
    // Cada vez que tocas el botón de arriba, actualizamos el formData
    setFormData(prev => ({
      ...prev,
      tipo_movimiento: modo
    }));
  }, [modo]); //  Si cambia el botón, ejecutá esto



  
  const loadAllData = async () => {
    setLoading(true);
    try {
      // 1. Cargar Gastos
      const resGastos = await apiFetch('/movimiento');
      if (resGastos.ok) {
        const data = await resGastos.json();
        setExpenses(data);
      }

      // 2. Cargar Categorías
      const resCat = await apiFetch('/categorias');
      if (resCat.ok) {
        const data = await resCat.json();
        setCategorias(data);
        if (data.length > 0 && !formData.categoria) {
            setFormData(prev => ({ ...prev, categoria: data[0] }));
        }
      }

      // 3. Cargar Medios de Pago
      const resMedio = await apiFetch('/medios-pago');
      if (resMedio.ok) {
        const data = await resMedio.json();
        setMediosPago(data);
        if (data.length > 0 && !formData.medio_pago) {
            setFormData(prev => ({ ...prev, medio_pago: data[0] }));
        }
      }
      const resTipoMovimiento= await apiFetch('/tipo_movimiento');
      if (resTipoMovimiento.ok) {
        const data = await resTipoMovimiento.json();
        setTipo_movimiento(data);
        if (data.length > 0 && !formData.tipo_movimiento) setFormData(prev => ({ ...prev, tipo_movimiento: data[0] }));
      }

    } catch (error) {
      console.error("Error cargando datos iniciales:", error);
    } finally {
      setLoading(false);
      cargarDatosCartera();
    }
  };

  // Recarga ligera solo de tabla
  const reloadExpenses = async () => {
    try {
        const res = await apiFetch('/movimiento');
        if (res.ok) setExpenses(await res.json());
    } catch (error) { console.error(error); }
  };

  // --- ACCIONES ---

  const handleLogout = () => {
    localStorage.clear(); // Borra token y usuario
    navigate("/login", { replace: true });
  };

const handleDeleteExpense = async (id_movimiento: number | undefined) => {
  if (!id_movimiento) return;

// 1. Buscamos el objeto en el estado local para sacar la descripción
  const movimientoData = expenses.find(exp => 
    (exp.ID_MOVIMIENTO || exp.id_movimiento) === id_movimiento
  );
  
  const nombreMovimiento = movimientoData?.descripcion || "este movimiento";
  const montoMovimiento = movimientoData?.monto ? `$${Number(movimientoData.monto).toLocaleString('es-AR')}` : "";

  // 1. Confirmación con estilo MyFinTrax
  const result = await Swal.fire({
    title: '¿Estás seguro?',
    html: `
          <div class="text-gray-700">
            Estás por eliminar: <br>
            <strong class="text-blue-900 text-lg uppercase">${nombreMovimiento}</strong> <br>
            <span class="text-gray-900 font-bold">${montoMovimiento}</span>
          </div>
        `,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#1e3a8a', // El azul de tu header
    cancelButtonColor: '#ef4444',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar',
    background: '#ffffff',
    color: '#1e3a8a'
  });

  if (!result.isConfirmed) return;

  // 2. Iniciamos el estado de carga (para el spinner del tacho)
  setDeletingId(id_movimiento);

  try {
    const response = await apiFetch(`/movimiento/${id_movimiento}`, { 
      method: 'DELETE' 
    });

    if (!response.ok) throw new Error('Error al eliminar');

    // 3. Actualizamos el estado local (optimistic UI)
    setExpenses(prev => prev.filter(exp => 
      (exp.ID_MOVIMIENTO || exp.id_movimiento) !== id_movimiento
    ));

    // 4. Recargamos datos del balance/gráficos
    await reloadExpenses();

    // 5. Alerta de éxito elegante
    Swal.fire({
      title: '¡Eliminado!',
      text: 'El movimiento fue borrado correctamente.',
      icon: 'success',
      timer: 1500,
      showConfirmButton: false
    });

  } catch (error: any) {
    console.error(error);
    Swal.fire({
      title: 'Error',
      text: 'No se pudo eliminar el movimiento',
      icon: 'error',
      confirmButtonColor: '#1e3a8a'
    });
  } finally {
    // 6. Quitamos el estado de carga
    setDeletingId(null);
  }
};

  // const handleSubmit = async () => {
  //   if (!formData.monto || parseFloat(formData.monto) <= 0) return alert('Por favor ingresa un monto válido');
  //   if (!formData.descripcion.trim()) return alert('Ingresa una descripción');
  //   if (/^\d+$/.test(formData.descripcion)) return alert("La descripción no puede ser solo números");
  //   if (!formData.categoria || !formData.medio_pago) return alert('Selecciona categoría y medio de pago');

  //   const now = new Date();
  //   const fechaConHora = `${formData.fecha} ${now.toTimeString().slice(0, 8)}`;

  //   setSaving(true);
  //   try {
  //     const response = await apiFetch('/movimiento', {
  //       method: 'POST',
  //       body: JSON.stringify({ ...formData, fecha: fechaConHora })
  //     });

  //     if (!response.ok) {
  //       const errData = await response.json();
  //       throw new Error(errData.error || 'Error al guardar');
  //     }

  //     alert('Movimiento guardado correctamente');
  //     await reloadExpenses();
      
  //     setFormData(prev => ({ ...prev, monto: '', descripcion: '' }));
  //     setShowForm(false);

  //   } catch (error: any) {
  //     console.error(error);
  //     alert('Error al guardar: ' + error.message);
  //   } finally {
  //     setSaving(false);
  //   }
  // };



  const handleSubmit = async () => {
  // Validaciones con SweetAlert para que no sean intrusivas
  if (!formData.monto || parseFloat(formData.monto) <= 0) {
    return Swal.fire('Monto inválido', 'Por favor ingresa un monto mayor a 0', 'warning');
  }
  if (!formData.descripcion.trim()) {
    return Swal.fire('Falta descripción', 'Ingresa una descripción para el movimiento', 'warning');
  }
  if (/^\d+$/.test(formData.descripcion)) {
    return Swal.fire('Descripción inválida', 'La descripción no puede contener solo números', 'warning');
  }
  if (!formData.categoria || !formData.medio_pago) {
    return Swal.fire('Campos incompletos', 'Selecciona categoría y medio de pago', 'warning');
  }

  const now = new Date();
  const horas = String(now.getHours()).padStart(2, '0');
  const minutos = String(now.getMinutes()).padStart(2, '0');
  const segundos = String(now.getSeconds()).padStart(2, '0');
  const fechaConHora = `${formData.fecha} ${horas}:${minutos}:${segundos}`;

  setSaving(true);
  try {
    const response = await apiFetch('/movimiento', {
      method: 'POST',
      body: JSON.stringify({ ...formData, fecha: fechaConHora })
    });

    if (!response.ok) {
      const errData = await response.json();
      throw new Error(errData.error || 'Error al guardar');
    }

    // --- VENTANA DE ÉXITO ---
    await Swal.fire({
      title: '¡Movimiento Guardado!',
      html: `Se registró <strong class="text-blue-900">${formData.descripcion}</strong> correctamente.`,
      icon: 'success',
      confirmButtonColor: '#16a34a', // Verde éxito
      timer: 2000,
      timerProgressBar: true
    });

    await reloadExpenses();
    setFormData(prev => ({ ...prev, monto: '', descripcion: '' }));
    setShowForm(false);

  } catch (error) {
    console.error(error);
    // --- VENTANA DE ERROR ---
    Swal.fire({
      title: 'Error al guardar',
      text: error.message || 'Ocurrió un error inesperado',
      icon: 'error',
      confirmButtonColor: '#ef4444' // Rojo error
    });
  } finally {
    setSaving(false);
  }
};

    // -- BALANCE -- //
  const [balanceMes, setBalanceMes] = useState<{
    ingresos: number;
    gastos: number;
    balance: number;
    porcentaje_ahorro: number;
  } | null>(null);

  useEffect(() => {
  const fetchBalance = async () => {
    try {
      const response = await apiFetch("/balance-mes", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      const data = await response.json();
      setBalanceMes(data);
    } catch (error) {
      console.error("Error trayendo balance:", error);
    }
  };

    fetchBalance();
  }, [expenses]);

  const porcentaje = balanceMes?.porcentaje_ahorro ?? 0;
  const porcentajeVisual = Math.min(Math.abs(porcentaje), 100);

   // --  -- //



  // --- UTILS VISUALES ---
  const handleNextSlide = () => setCurrentSlide(prev => (prev + 1) % 3);
  const handleChange = (field: keyof FormData, value: string) => setFormData({ ...formData, [field]: value });
  const formato_moneda = (value: string) => (!value ? '' : Number(value).toLocaleString('es-AR'));
  const limpiamos_a_numero = (value: string) => value.replace(/\D/g, '');

  const formatearFechaHora = (fecha: string) => {
    const d = new Date(fecha);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');

    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');

    return `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;
  };




  // --- CÁLCULOS GRÁFICOS ---
  const getChartData = () => {
    const categoryTotals: Record<string, number> = {};
    expenses.forEach(exp => {
      const tipo = exp.tipo_movimiento || exp.TIPO_movimiento;
      if (tipo === "GASTO") {
      const cat = exp.CATEGORIA || exp.categoria || 'Varios';
      const monto = exp.MONTO || exp.monto || 0;
      categoryTotals[cat] = (categoryTotals[cat] || 0) + parseFloat(monto.toString());
      }
    });
    return Object.entries(categoryTotals).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  };

  const getCurrentMonthChartData = () => {
    const now = new Date();
    const currentMonth = now.getMonth(); 
    const currentYear = now.getFullYear();
    const categoryTotals: Record<string, number> = {};
    expenses.forEach(exp => {
      const expDate = new Date(exp.FECHA || exp.fecha || '');
      const fecha = getFecha(exp);
      const tipo = exp.tipo_movimiento || exp.TIPO_movimiento;
      if (expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear && exp.tipo_movimiento === "GASTO") {
        const cat = exp.CATEGORIA || exp.categoria || 'Varios';
        const monto = parseFloat((exp.MONTO || exp.monto || 0).toString());
        categoryTotals[cat] = (categoryTotals[cat] || 0) + monto;
      }
    });
    return Object.entries(categoryTotals).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  };


  // const total = expenses.reduce((sum, exp) => sum + parseFloat((exp.MONTO || exp.monto || 0).toString()), 0);
  
  // const totalMes = expenses.reduce((sum, exp) => {
  //   const now = new Date();
  //   const expDate = new Date(exp.FECHA || exp.fecha || '');
  //   if (expDate.getMonth() === now.getMonth() && expDate.getFullYear() === now.getFullYear()) {
  //       return sum + parseFloat((exp.MONTO || exp.monto || 0).toString());
  //   }
  //   return sum;
  // }, 0);


    // --- TOTALES ---
  expenses.forEach(exp => {
    console.log("Movimiento:", {
      tipo: exp.TIPO_movimiento,
      tipo_alt: exp.tipo_movimiento,
      raw: exp
    });
  });

  const getTipo = (exp: any) => {
    const valor =
      exp.TIPO_movimiento ??
      exp.tipo_moviento ??
      exp.tipo_movimiento ??
      exp.ID_TIPO_MOVIMIENTO ??
      exp.id_tipo_movimiento;

    return valor; // 
  };

  const getMonto = (exp: any) => {
    return Number(exp.MONTO ?? exp.monto ?? 0);
  };

  const getFecha = (exp: any) => {
    return new Date(exp.FECHA ?? exp.fecha ?? '');
  };

  const total = expenses.reduce((sum, exp) => {
    const fecha = getFecha(exp);

    if (
      fecha.getFullYear() === Number(selectedYear) &&
      exp.tipo_movimiento === "GASTO"
    ) {
      return sum + getMonto(exp);
    }

    return sum;
  }, 0);

  const totalIngresadoTodoElAnio = expenses.reduce((sum, exp) => {
  const fecha = getFecha(exp);

  if (
    fecha.getFullYear() === Number(selectedYear) &&
    exp.tipo_movimiento === "INGRESO"
  ) {
    return sum + getMonto(exp);
  }

  return sum;
}, 0);

  const now = new Date();
  const totalMes = expenses.reduce((sum, exp) => {
    const fecha = getFecha(exp);

    if (
      fecha.getMonth() === now.getMonth() &&
      fecha.getFullYear() === now.getFullYear() &&
      exp.tipo_movimiento === "GASTO"
    ) {
      return sum + getMonto(exp);
    }

    return sum;
  }, 0);

  const totalIngresadoMes = expenses.reduce((sum, exp) => {
    const fecha = getFecha(exp);

    if (
      fecha.getMonth() === now.getMonth() &&
      fecha.getFullYear() === now.getFullYear() &&
      exp.tipo_movimiento === "INGRESO"
    ) {
      return sum + getMonto(exp);
    }

    return sum;
  }, 0);

    // ---  ---
  const statsCategoriaMes = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    return expenses.reduce((acc, exp) => {
      const expDate = new Date(exp.FECHA || exp.fecha || '');
      if (expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear && exp.tipo_movimiento === "GASTO") {
        const nombreCat = exp.CATEGORIA || exp.categoria || 'Varios';
        const monto = parseFloat((exp.MONTO || exp.monto || 0).toString());

        acc.totales[nombreCat] = (acc.totales[nombreCat] || 0) + monto;
        if (acc.totales[nombreCat] > acc.maxMonto) {
          acc.maxMonto = acc.totales[nombreCat];
          acc.maxNombre = nombreCat;
        }
      }
      return acc;
    }, { totales: {} as Record<string, number>, maxNombre: 'N/A', maxMonto: 0 });
  }, [expenses]);


  const chartData = getChartData();
  const chartDataMes = getCurrentMonthChartData();
  const maxCategory = chartData.length > 0 ? chartData[0] : null;

  const availableYears = useMemo(() => {
    const years = new Set(expenses.map(g => new Date(g.FECHA || g.fecha || '').getFullYear()));
    return Array.from(years).sort((a, b) => b - a);
  }, [expenses]);



    //-- calculamos tipo de gastos.
  const chartDataTipoGasto = useMemo(() => {
  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  
  const totals = expenses.reduce((acc, exp) => {
    const expDate = new Date(exp.FECHA || exp.fecha || '');
    
    // Filtramos por mes actual para que coincida con la torta de categorías
    if (expDate.getMonth() === currentMonth && expDate.getFullYear() === currentYear) {
      const tipo = exp.TIPO_gasto || exp.tipo_gasto || 'VARIABLE';
      const monto = parseFloat((exp.MONTO || exp.monto || 0).toString());
      acc[tipo] = (acc[tipo] || 0) + monto;
    }
    return acc;
  }, {} as Record<string, number>);

  return Object.entries(totals).map(([name, value]) => ({ name, value }));
}, [expenses]);


  const nombreMesActual = new Intl.DateTimeFormat('es-AR', { month: 'long' }).format(new Date());

const statsAnoBarras = useMemo(() => {
  const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
  
  // Inicializamos cada mes con ambos valores en 0
  const data = meses.map(m => ({ 
    name: m, 
    gastos: 0, 
    ingresos: 0 
  }));

  expenses.forEach(mov => {
    // Usamos tu lógica de obtener fecha
    const fecha = new Date(mov.FECHA || mov.fecha || '');
    
    if (fecha.getFullYear().toString() === selectedYear) {
      const monto = parseFloat((mov.MONTO || mov.monto || 0).toString());
      const mesIndex = fecha.getMonth();

      if (!isNaN(mesIndex)) {
        // Separamos por tipo de movimiento
        if (mov.tipo_movimiento === "GASTO") {
          data[mesIndex].gastos += monto;
        } else if (mov.tipo_movimiento === "INGRESO") {
          data[mesIndex].ingresos += monto;
        }
      }
    }
  });

  return { chartData: data };
}, [expenses, selectedYear]);


//--------------------------------------------------------------------------------------------
// --- BUSCADOR ---
  const [searchTerm, setSearchTerm] = useState("");
  // Creamos una lista filtrada que se recalcula cuando cambia el texto o los gastos
  const filteredExpenses = useMemo(() => {
    if (!searchTerm) return expenses; // Si no hay búsqueda, devuelve todo
    const lowerTerm = searchTerm.toLowerCase();
    return expenses.filter(g => {
      // Buscamos en Descripción y Categoría
      const desc = (g.DESCRIPCION || g.descripcion || '').toLowerCase();
      const cat = (g.CATEGORIA || g.categoria || '').toLowerCase();
      // Opcional: También buscar por monto
      const monto = (g.MONTO || g.monto || '').toString();

      return desc.includes(lowerTerm) || cat.includes(lowerTerm) || monto.includes(lowerTerm);
    });
  }, [expenses, searchTerm]);

  //--------------------------------------------------------------------------------------------


   // --- RENDER ---
  if (loading) {
    return (
      <div className="min-h-screen  flex items-center justify-center">
        <div className="text-center p-6 bg-white rounded-xl shadow-lg">
          <RefreshCw className="animate-spin mx-auto mb-4 text-blue-600" size={48} />
          <div className="text-lg font-medium text-gray-900">Cargando tu Datos...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20 md:pb-12 fondo_personalizado ">
      
      {/*------------------ HEADER ----------------*/}
      <div className="bg-blue-950 shadow-sm sticky top-0 z-10 rounded-b-xl ">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-start items-center py-4 gap-4 md:gap-12">
            <div className="text-center md:text-left">
              <h1 className="text-3xl font-black text-white tracking-tighter">
                  <span className="text-green-500">My</span>Fin<span className="text-green-500">Trax</span>
                </h1>
              <p className="text-sm text-gray-100">Hola, <span className="font-bold text-green-500">{nombreAMostrar}</span></p>
            </div>
            
            {/* MENÚ DE ESCRITORIO */}
          <div className="hidden md:flex bg-blue-900/30 p-1 rounded-lg gap-2 border border-gray-100/20 backdrop-blur-sm">
            {/* Botón Añadir */}
            <button 
              onClick={() => setVistaActual('Registrar')}
              className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all flex items-center gap-2 outline-none
                ${vistaActual === 'Registrar' 
                  ? 'bg-white text-blue-950 shadow-md scale-105' 
                  : 'text-blue-100 bg-transparent'}`} 
            >
              <PiggyBank size={16}/> 
              <span>Añadir</span>
            </button>

            {/* Botón Movimientos */}
            <button 
              onClick={() => setVistaActual('inicio')}
              className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all flex items-center gap-2 outline-none
                ${vistaActual === 'inicio' 
                  ? 'bg-white text-blue-950 shadow-md scale-105' 
                  : 'text-blue-100 bg-transparent'}`}
            >
              <Home size={16}/> 
              <span>Movimientos</span>
            </button>

            {/* Botón Balance */}
            <button 
              onClick={() => setVistaActual('balance')}
              className={`px-4 py-1.5 text-sm font-bold rounded-md transition-all flex items-center gap-2 outline-none
                ${vistaActual === 'balance' 
                  ? 'bg-white text-blue-950 shadow-md scale-105' 
                  : 'text-blue-100 bg-transparent'}`}
            >
              <PieChartIcon size={16}/> 
              <span>Balance</span>
            </button>
          </div>

            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => loadAllData()} className="flex-1 md:flex-none justify-center bg-green-200 hover:bg-green-400 text-green-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-all text-sm font-medium">
                <RefreshCw size={18} /> <span className="hidden sm:inline">Actualizar</span>
              </button>
              <button onClick={handleLogout} className="flex-1 md:flex-none justify-center bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg flex items-center gap-2 transition-all text-sm font-medium">
                Salir
              </button>
            </div>
          </div>
        </div>
        {/* ===================== AÑADIMOS INFORMACION DE CIPTO ===================== */}
          <div className="w-full">
                <CryptoCarousel />
          </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">

        {/* ===================== VISTA DE Añadir ===================== */}
  {vistaActual === 'Registrar' && (
  <div className="max-w-md mx-auto animate-fade-in-up">
    {/* Botón para abrir/cerrar el formulario (Opcional si ya estás en esta vista) */}
    <div className="lg:col-span-1 space-y-6">
      <button
        onClick={() => setShowForm(!showForm)}
        className={`w-full py-4 rounded-xl shadow-md font-bold text-lg flex items-center justify-center gap-2 transition-all ${
          showForm ? 'bg-gray-100 text-gray-700 border border-gray-300' : 'bg-blue-900 text-white hover:bg-green-700'
        }`}
      >
        {showForm ? <X size={24} /> : <Plus size={24} />}
        {showForm ? 'Cancelar Registro' : 'Nuevo Movimiento'}
      </button>

      {/* FORMULARIO PROPIAMENTE DICHO */}
      {showForm && (
        <div className={`bg-white rounded-xl shadow-lg p-6 border transition-all duration-500 ${
            modo === 'INGRESO' 
              ? 'border-green-500 shadow-green-100' 
              : 'border-red-500 shadow-red-100'
          }`}>
          <div className="flex bg-red-900/10 backdrop-blur-md p-1.5 rounded-xl mb-8 border border-white/10 shadow-inner">
            <button
              onClick={() => setModo('GASTO')}
              className={`flex-1 py-2.5 text-xs sm:text-sm font-blackrounded-lg transition-all duration-300 flex items-center justify-center gap-2 
                ${modo === 'GASTO' 
                  ? 'bg-red-500 text-white shadow-[0_0_15px_rgba(239,68,68,0.4)] scale-100' 
                  : 'bg-gray-500 text-gray-100 hover:text-gray-100 scale-95 opacity-70'}`}
            >
              <ArrowDownCircle size={18} className={modo === 'GASTO' ? 'animate-pulse' : ''} />
              GASTOS
            </button>

            <button
              onClick={() => setModo('INGRESO')}
              className={`flex-1 py-2.5 text-xs sm:text-sm font-black rounded-lg transition-all duration-300 flex items-center justify-center gap-2 
                ${modo === 'INGRESO' 
                  ? 'bg-green-500 text-white shadow-[0_0_15px_rgba(34,197,94,0.4)] scale-100' 
                  : 'bg-gray-500 text-gray-100 hover:text-gray-100 scale-95 opacity-70'}`}
            >
              <ArrowUpCircle size={18} className={modo === 'INGRESO' ? 'animate-pulse' : ''} />
              INGRESOS
            </button>
          </div>


          <h1 className={`text-2xl md:text-2xl font-bold text-center text-gray-600 tracking-wide border-b-2 border-gray-300 pb-2 mb-4 drop-shadow-[0_2px_2px_rgba(0,0,0,0.15)] transition-colors duration-500 ${
            modo === 'INGRESO' ? 'text-green-600' : 'text-red-600'
          }`}
          >
            REGISTRAR {modo}
          </h1>

          <div className="space-y-4">
            {/* Fecha */}
            <div>
              <label className="text-sm font-medium text-gray-900 block mb-1">Fecha</label>
              <input 
                type="date" 
                value={formData.fecha} 
                onChange={(e) => handleChange('fecha', e.target.value)} 
                className="w-full px-4 py-2 bg-gray-50 text-gray-900 border rounded-lg outline-none" 
              />
            </div>

            {/* Tipo Movimiento */}
            <div>
              <label className="text-sm font-medium text-gray-400 block mb-1">
                Tipo Movimiento
              </label>
              <select 
                value={formData.tipo_movimiento} 
                disabled // Bloqueado para que no lo cambien a mano
                className={`w-full px-4 py-2 border rounded-lg outline-none transition-all duration-300 appearance-none bg-gray-200 cursor-not-allowed font-bold
                  ${modo === 'GASTO' ? 'border-red-500/50 text-red-400' : 'border-green-500/50 text-green-800'}`}
              >
                <option value="">Seleccione tipo</option>
                <option value="GASTO">GASTO</option>
                <option value="INGRESO">INGRESO</option>
              </select>
              <p className="text-[10px] text-gray-500 mt-1 italic">
                * Campo vinculado a la selección superior
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Monto */}
              <div className="relative">
                <label className="text-sm font-medium text-gray-700 block mb-1">Monto</label>
                <span className="absolute left-3 top-1/2 -translate-y-1/6 text-green-900 font-medium">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formato_moneda(formData.monto)}
                  onChange={(e) => handleChange('monto', limpiamos_a_numero(e.target.value))}
                  className="w-full pl-8 pr-4 py-2 bg-red-50 border border-gray-300 rounded-lg outline-none text-gray-900"
                />
              </div>
              {/* Clase (Fijo/Variable) */}
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Clase</label>
                <select 
                  value={formData.tipo_gasto} 
                  onChange={(e) => handleChange('tipo_gasto', e.target.value)} 
                  className="w-full px-4 py-2 bg-gray-50 border rounded-lg outline-none"
                >
                  <option value="FIJO">Fijo</option>
                  <option value="VARIABLE">Variable</option>
                </select>
              </div>
            </div>

            {/* Categoría */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Categoría</label>
              <select 
                value={formData.categoria} 
                onChange={(e) => handleChange('categoria', e.target.value)} 
                className="w-full px-4 py-2 bg-gray-50 border rounded-lg outline-none"
              >
                {categorias.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            {/* Medio de Pago */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Medio de Pago</label>
              <select 
                value={formData.medio_pago} 
                onChange={(e) => handleChange('medio_pago', e.target.value)} 
                className="w-full px-4 py-2 bg-gray-50 border rounded-lg outline-none"
              >
                {mediosPago.map(medio => <option key={medio} value={medio}>{medio}</option>)}
              </select>
            </div>

            {/* Descripción */}
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Descripción</label>
              <input 
                type="text" 
                placeholder="Ej: Gaseosa Sprite" 
                value={formData.descripcion} 
                onChange={(e) => handleChange('descripcion', e.target.value)} 
                className="w-full px-4 py-2 text-gray-900 bg-blue-300/50  border rounded-lg outline-none placeholder:text-gray-500" 
              />
            </div>

            {/* Botón Guardar */}
            <button 
              onClick={handleSubmit} 
              disabled={saving} 
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg shadow flex justify-center items-center gap-2 mt-2 transition-transform active:scale-95"
            >
              {saving ? <RefreshCw className="animate-spin" size={20} /> : 'Confirmar Guardado'}
            </button>
          </div>
        </div>
      )}
    </div>

          {!showForm && (
                <div className="animate-fade-in"> 
                  <div className="space-y-6 mt-6">
                    <FormularioActivos 
                      idUsuario={usuarioId} 
                      onActualizar={cargarDatosCartera} 
                    />
                  </div>

                  <div className="space-y-6 mt-6">
                    <Billetera 
                      activos={misActivos} 
                      total={totalPatrimonio} 
                      onActualizar={cargarDatosCartera}
                    />
                  </div>
                </div>
              )}

  </div>
)}


        {/* ===================== VISTA DE BALANCE ===================== */}
        {vistaActual === 'balance' && (
          <div className="animate-fade-in">
            
            {/* =====================  BALANCE ===================== */}
            {balanceMes && (
              <div className="bg-gray-100 p-6 rounded-2xl shadow-md mt-6  mb-10">
                <div className="absolute -top-24 -right-24 w-48 h-48 bg-emerald-500/10 blur-[100px] rounded-full group-hover:bg-emerald-500/20 transition-all duration-700" />
                <h1 className="text-x1 uppercase  text-center md:text-2xl font-extrabold text-gray-900 tracking-tight font-sans not-italic mb-2">
                  Salud financiera del mes
                </h1>

              <div className="relative w-full bg-gray-200 rounded-full h-6 overflow-hidden shadow-inner flex items-center">                  
                <div
                    className={`h-6 transition-all duration-700 ${
                      porcentaje > 20
                        ? "bg-green-500"
                        : porcentaje > 0
                        ? "bg-yellow-500"
                        : "bg-red-500"
                    }`}
                    style={{ width: `${porcentajeVisual}%` }}
                  />
                  {/* TEXTO DENTRO DE LA BARRA (Centrado absoluto) */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-gray-100 font-extrabold text-xl drop-shadow-md">
                      Diferencia: $ {(balanceMes.ingresos - balanceMes.gastos).toLocaleString("es-AR")}
                    </span>
                  </div>
                </div>

                <div className="mt-4 flex justify-between text-sm">
                  <span className='text-emerald-800 font-extrabold tracking-wider'>AL ${balanceMes.ingresos.toLocaleString("es-AR")} ▲ </span>
                  <span className='text-red-800 font-extrabold' >BA  ${balanceMes.gastos.toLocaleString("es-AR")} ▼</span>
                </div>

                  <p className={`mt-3 font-bold text-center relative top-[10px]  ${
                      porcentaje >= 0 ? "text-emerald-900" : "text-red-500"
                    }`}>
                    {porcentaje >= 0
                      ? `${porcentaje.toFixed(1)}% de ahorro ▲`
                      : `${porcentaje.toFixed(1)}% de déficit ▼`}
                  </p>
              </div>
            )}


              <div className="flex items-center gap-1">
                  {/* Botón del Ojo a la Izquierda */}
                    <button 
                      onClick={() => setOcultarDinero(!ocultarDinero)}
                      className=" mb-5    flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-500 hover:text-blue-900 hover:border-blue-200 rounded-full transition-all shadow-sm active:scale-95"
                      title={ocultarDinero ? "Mostrar saldo" : "Ocultar saldo"}
                    >
                      {ocultarDinero ? <EyeOff size={18} /> : <Eye size={18} />}
                      <span className="text-xs font-semibold uppercase tracking-wider">
                        {ocultarDinero ? "Mostrar" : "Ocultar"}
                      </span>
                    </button>

             </div>
             {/* TARJETAS ESTADÍSTICAS */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-green-200 rounded-xl shadow-sm p-4 border-l-[8px] border-green-700 flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Total Ingresado en {nombreMesActual.charAt(0).toUpperCase() + nombreMesActual.slice(1)}</p>
                  <p className="text-3xl font-black text-gray-900 tracking-tight">
                      {ocultarDinero ? (
                        <span className="text-2xl tracking-widest font-mono">*****</span>
                      ) : (
                        `$ ${totalIngresadoMes.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`
                      )}
                    </p>
                </div>
                <div className="p-3 bg-green-50 rounded-full"><DollarSign className="text-green-600" size={24} /></div>
              </div>



              <div className="bg-red-200 rounded-xl shadow-sm p-4 border-l-[8px] border-red-500 flex justify-between items-center transition-all duration-300">
                    <div>
                      <p className="text-sm font-medium text-gray-500 mb-1">
                        Total Gastado en {nombreMesActual.charAt(0).toUpperCase() + nombreMesActual.slice(1)}
                    </p>
                    <p className="text-3xl font-black text-gray-900 tracking-tight">
                      {ocultarDinero ? (
                        <span className="text-2xl tracking-widest font-mono">*****</span>
                      ) : (
                        `$ ${totalMes.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`
                      )}
                    </p>
                  </div>
                    
                  <div className="flex items-center gap-1">
                </div>

                {/* Icono de la moneda a la derecha (opcional, para balancear) */}
                <div className="p-3 bg-green-50 rounded-full">
                  <DollarSign className="text-green-600" size={24} />
                </div>
              </div>

              
              {/* //-- Total ingresado para todo el año --// */}
              <div className="bg-yellow-200 rounded-xl shadow-md p-5 border-l-[8px] border-yellow-600 flex justify-between items-center transition-all hover:shadow-lg">
                <div>
                  <p className="text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">
                    Total Ingresado {selectedYear}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-black text-gray-900">
                      {ocultarDinero ? (
                        <span className="text-2xl tracking-widest font-mono">*****</span>
                      ) : totalIngresadoTodoElAnio > 0 ? (
                        `$ ${totalIngresadoTodoElAnio.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`
                      ) : (
                        <span className="text-sm font-medium text-gray-400 italic">
                          Sin registros en {selectedYear}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                
                {/* El icono ahora combina mejor con el color violeta del borde */}
                <div className="p-3 bg-green-50 rounded-full"><Bitcoin className="text-orange-600" size={24} /></div>
              </div>

              <div className="bg-violet-200 rounded-xl shadow-md p-5 border-l-[8px] border-violet-500 flex justify-between items-center transition-all hover:shadow-lg">
                <div>
                  <p className="text-xs uppercase tracking-wider font-bold text-gray-400 mb-1">Total Gastado {selectedYear}</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {/* {total === totalMes ? 
                      <span className="text-sm font-medium text-gray-400">No hay registros históricos</span> : 
                      `$ ${total.toLocaleString('es-AR', {minimumFractionDigits: 0})}`
                    } */}
                    {ocultarDinero ? (
                        <span className="text-2xl tracking-widest font-mono">*****</span>
                      ) : totalIngresadoTodoElAnio > 0 ? (
                        `$ ${total.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`
                      ) : (
                        <span className="text-sm font-medium text-gray-400 italic">
                          Sin registros en {selectedYear}
                        </span>
                      )}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-full"><Calendar className="text-orange-600" size={24} /></div>
              </div>
              
              <div className="bg-white rounded-xl shadow-sm p-4 border-l-[8px] border-red-500 flex justify-between items-center">
                <div>
                  <p className="text-sm font-medium text-gray-500 mb-1">Categoría Mayor Gasto</p>
                  <p className="text-1xl font-bold text-red-800"> {statsCategoriaMes.maxNombre}</p>
                  <p className="text-2xl font-extrabold text-gray-900">
                    {/* ${statsCategoriaMes.maxMonto.toLocaleString('es-AR', { minimumFractionDigits: 0 })} */}
                    {ocultarDinero ? (
                        <span className="text-2xl tracking-widest font-mono">*****</span>
                      ) : totalIngresadoTodoElAnio > 0 ? (
                        `$ ${statsCategoriaMes.maxMonto.toLocaleString('es-AR', { minimumFractionDigits: 0 })}`
                      ) : (
                        <span className="text-sm font-medium text-gray-400 italic">
                          Sin registros en {selectedYear}
                        </span>
                      )}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-full"><ScanEye className="text-red-600" size={24} /></div>
              </div>
            </div>

           {/* ZONA DE GRÁFICOS */}
    {expenses.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* GRÁFICO CIRCULAR */}
          <div className="bg-white rounded-xl shadow-sm p-3 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-800">
                  {currentSlide === 0 && "Distribución Mes Actual (Gastos)"}
                  {currentSlide === 1 && "Distribución Histórica (Gastos)"}
                  {currentSlide === 2 && "Fijo vs. Variable (Mes)"}
                </h3>
              </div>

              <div className="h-80 w-full flex items-center justify-center">
                {/* LÓGICA DE VALIDACIÓN DE DATOS */}
                {(currentSlide === 0 ? chartDataMes : currentSlide === 1 ? chartData : chartDataTipoGasto).length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={
                          currentSlide === 0 ? chartDataMes : 
                          currentSlide === 1 ? chartData : 
                          chartDataTipoGasto
                        }
                        cx="50%" cy="40%" 
                        innerRadius={60} outerRadius={80} paddingAngle={5}
                        dataKey="value" 
                        animationDuration={500}
                        label={({ value, percent }) => {
                          if (currentSlide === 2) return `${(percent * 100).toFixed(0)}%`;
                          return `$${value.toLocaleString('es-AR')}`;
                        }}
                      >
                        {currentSlide === 2
                          ? chartDataTipoGasto.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.name === 'FIJO' ? '#4F46E5' : '#FB923C'} />
                            ))
                          : (currentSlide === 0 ? chartDataMes : chartData).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))
                        }
                      </Pie>
                      <Tooltip formatter={(value) => `$ ${value.toLocaleString('es-AR')}`} />
                      <Legend verticalAlign="bottom" height={36} wrapperStyle={{ bottom: 35 }} iconType="circle" />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  /* MENSAJE CUANDO NO HAY REGISTROS */
                  <div className="text-center">
                    <span className="text-sm font-medium text-gray-400 italic">
                      No hay registros de este mes
                    </span>
                  </div>
                )}
              </div>

          <button 
            onClick={handleNextSlide} 
            className="px-3 py-1 bg-blue-900 hover:bg-blue-700 text-white text-xs font-bold rounded-full transition-all mt-4"
          >
            {currentSlide === 0 && "Ver Histórico →"}
            {currentSlide === 1 && "Fijo - Variable →"}
            {currentSlide === 2 && "← Ver Mes Actual"}
          </button>
        </div>
      </div>


                {/* GRÁFICO DE BARRAS */}
                <div className="bg-white rounded-xl shadow-sm p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold text-gray-800">Evolución Anual</h3>
                    <select 
                      value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}
                      className="border border-gray-300 rounded px-2 py-1 text-sm outline-none"
                    >
                      {availableYears.length === 0 && <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>}
                      {availableYears.map(ano => <option key={ano} value={ano}>{ano}</option>)}
                    </select>
                  </div>
                    <div className="h-80 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={statsAnoBarras.chartData}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                          <XAxis 
                            dataKey="name" 
                            tick={{fontSize: 12, fill: '#6B7280'}} 
                            axisLine={false}
                            tickLine={false}
                          />
                          <YAxis 
                            tick={{fontSize: 12, fill: '#6B7280'}} 
                            tickFormatter={(val) => `$${val}`} 
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip 
                            cursor={{fill: '#F3F4F6'}}
                            formatter={(value: number, name: string) => [
                              `$ ${value.toLocaleString('es-AR')}`, 
                              name === 'Ingresos' ? 'Ingresos' : 'Gastos'
                            ]} 
                          />
                          <Legend verticalAlign="top" align="right" height={36}/>
                          
                          {/* Barra de Ingresos (Verde) */}
                          <Bar 
                            name="Ingresos"
                            dataKey="ingresos" 
                            fill="#10B981" 
                            radius={[4, 4, 0, 0]} 
                          />
                          
                          {/* Barra de Gastos (Rojo) */}
                          <Bar 
                            name="Gastos"
                            dataKey="gastos" 
                            fill="#EF4444" 
                            radius={[4, 4, 0, 0]} 
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                </div>
              </div>
            ) : (
              <div className="bg-white p-12 text-center rounded-xl text-gray-400 mb-8">
                No hay suficientes datos para armar el balance todavía.
              </div>
            )}
          </div>
        )}

        {/* ===================== VISTA DE INICIO ( LISTA) ===================== */}
        {vistaActual === 'inicio' && (
          <div className="animate-fade-in max-w-5xl mx-auto px-4 pb-10">
            
            {/* LISTA DE Movimientos */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-t-xl shadow-sm overflow-hidden">
                <div className="bg-blue-950 p-6 flex flex-col sm:flex-row justify-between items-center gap-4 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl md:text-2xl font-extrabold text-gray-100 tracking-tight font-sans not-italic mb-2">ÚLTIMOS <span className="text-green-500">MOVIMIENTOS</span> </h1>
                    <span className="text-xs font-bold px-4 py-2 bg-white text-gray-900 rounded-full">{filteredExpenses.length}</span>
                  </div>
                  <div className="relative w-full sm:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-900" size={18} />
                    <input type="text" placeholder="Buscar Movimientos..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-gray-50 border rounded-lg outline-none text-gray-900 placeholder:text-gray-500"/>
                  </div>
                </div>

                {filteredExpenses.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">
                    {searchTerm ? <p>No se encontraron resultados</p> : <p>No hay movimientos registrados.</p>}
                  </div>
                ) : (
                  <>
                    {/* Vista mobile Lista */}
                    <div className="block md:hidden">
                      {filteredExpenses.slice(0, 10).map((exp, idx) => (
                        <div key={idx} className="p-4 border-b hover:bg-gray-200">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <span className="text-xs font-semibold text-gray-400 uppercase">{exp.CATEGORIA || exp.categoria}</span>
                              <h3 className="text-gray-900 font-medium">{exp.DESCRIPCION || exp.descripcion || '-'}</h3>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full font-bold ${(exp.TIPO_gasto || exp.tipo_gasto) === 'FIJO' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {exp.TIPO_gasto || exp.tipo_gasto}
                            </span>
                          </div>
                          <div className="flex justify-between items-end mt-3">
                            <div className="space-y-1">
                              <div className="flex items-center text-gray-500 text-xs gap-1"><Calendar size={12} /> {formatearFechaHora(exp.FECHA || exp.fecha || '')}</div>
                              <div className="flex items-center text-gray-500 text-xs gap-1"><CreditCard size={12} /> {exp.MEDIO_PAGO || exp.medio_pago}</div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className={`text-lg font-bold whitespace-nowrap ${
                                  (exp.TIPO_movimiento === "INGRESO" || exp.tipo_movimiento === "INGRESO") 
                                    ? "text-green-600" 
                                    : "text-red-600"
                                }`}>
                                  {(exp.TIPO_movimiento === "INGRESO" || exp.tipo_movimiento === "INGRESO") ? "+ " : "- "} 
                                  $ {parseFloat((exp.MONTO || exp.monto || 0).toString()).toLocaleString('es-AR')}
                                </span>
                                <button 
                                  onClick={() => handleDeleteExpense(exp.ID_MOVIMIENTO || exp.id_movimiento)} 
                                  className="text-gray-100 hover:text-red-600 p-1 "
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Vista Escritorio Tabla */}
                    <div className="hidden md:block overflow-x-auto">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wider border-b">
                            <th className="px-6 py-4 font-semibold">Fecha</th>
                            <th className="px-6 py-4 font-semibold">Categoría</th>
                            <th className="px-6 py-4 font-semibold">Descripción</th>
                            <th className="px-6 py-4 font-semibold">Monto</th>
                            <th className="px-6 py-4 font-semibold text-center">Eliminar</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filteredExpenses.slice(0, 10).map((exp, idx) => (
                            <tr key={idx} className="hover:bg-blue-50 group">
                              <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{formatearFechaHora(exp.FECHA || exp.fecha || '')}</td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col gap-1 items-center w-full">
                                  <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-[10px] uppercase font-bold tracking-wider">{exp.CATEGORIA || exp.categoria}</span>
                                  <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold border ${(exp.TIPO_gasto || exp.tipo_gasto) === 'FIJO' ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'bg-emerald-100 text-emerald-700 border-emerald-200'}`}>{exp.TIPO_gasto || exp.tipo_gasto}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-700">
                                  <div className="flex flex-col">
                                      <span className="font-medium">{exp.DESCRIPCION || exp.descripcion || '-'}</span>
                                      <span className="text-xs text-gray-400">{exp.MEDIO_PAGO || exp.medio_pago}</span>
                                  </div>
                              </td>
                              <td className={`px-6 py-4 text-sm font-bold whitespace-nowrap ${
                                                                (exp.TIPO_movimiento === "INGRESO" || exp.tipo_movimiento === "INGRESO") 
                                                                  ? "text-green-600" 
                                                                  : "text-red-600"
                                                              }`}>
                                                                {(exp.TIPO_movimiento === "INGRESO" || exp.tipo_movimiento === "INGRESO") ? "+ " : "- "} 
                                                                $ {parseFloat((exp.MONTO || exp.monto || 0).toString()).toLocaleString('es-AR', {minimumFractionDigits: 2})}
                              </td>
                              <td className="px-6 py-4 text-center">
                                <button onClick={() => handleDeleteExpense(exp.ID_MOVIMIENTO || exp.id_movimiento)} className="text-center text-gray-400 hover:text-red-600  transition-colors p-1 bg-transparent border-none outline-none">
                                  {deletingId === (exp.ID_MOVIMIENTO || exp.id_movimiento) ? <RefreshCw className="animate-spin" size={16} /> : <Trash2 size={16} />}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* ===================== BOTTOM NAVIGATION BAR ===================== */}
      <div className="fixed bottom-5 left-4 right-4 bg-gray-900/95 backdrop-blur-sm border border-green-800 flex justify-around items-end h-16 md:hidden z-[100] rounded-3xl shadow-2xl">
        
        {/* BUTTON INICIO */}
        <button 
          onClick={() => setVistaActual('inicio')}
          className={`flex-1 flex flex-col items-center justify-center transition-all duration-300 rounded-2xl
            ${vistaActual === 'inicio' 
              ? 'bg-blue-600 text-white h-[90%] -translate-y-4 shadow-[0_-10px_20px_rgba(37,99,235,0.4)] scale-105' 
              : 'bg-transparent text-gray-400 h-full'
            }`}
        >
          <Home size={26} />
          <span className="text-[11px] mt-1 font-bold">Inicio</span>
        </button>

        {/* BUTTON REGISTRAR */}
        <button 
          onClick={() => setVistaActual('Registrar')}
          className={`flex-1 flex flex-col items-center justify-center transition-all duration-300 rounded-2xl
            ${vistaActual === 'Registrar' 
              ? 'bg-blue-600 text-white h-[90%] -translate-y-4 shadow-[0_-10px_20px_rgba(34,197,94,0.4)] scale-105' 
              : 'bg-transparent text-gray-400 h-full'
            }`}
        >
          <PiggyBank size={26} />
          <span className="text-[11px] mt-1 font-bold">Añadir</span>
        </button>

        {/* BUTTON BALANCE */}
        <button 
          onClick={() => setVistaActual('balance')}
          className={`flex-1 flex flex-col items-center justify-center transition-all duration-300 rounded-2xl
            ${vistaActual === 'balance' 
              ? 'bg-blue-600 text-white h-[90%] -translate-y-4 shadow-[0_-10px_20px_rgba(124,58,237,0.4)] scale-105' 
              : 'bg-transparent text-gray-400 h-full'
            }`}
        >
          <PieChartIcon size={26} />
          <span className="text-[11px] mt-1 font-bold">Balance</span>
        </button>
      </div>

      {/* ===================== FOOTER ===================== */}
      
      <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12 mb-24 md:mb-4"> 
        {/* mb-24 asegura que el menú móvil no tape el copyright */}
        <div className="border-t border-gray-200 pt-8 pb-4 text-center">
          {/* Quitamos 'hidden' y 'md:block' */}
          <p className="text-lg text-gray-100 font-medium">
            &copy; {new Date().getFullYear()} <span className="text-indigo-300 font-bold"> @LAS</span>. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default Dashboard;